/**
 * Supplier portal lists: what each page shows and how a row reads. Pure helpers, so the rules can
 * be tested without rendering the page.
 *
 * Columns are the supplier's own from OrdersTableColumns (role supplier), in the same order and with
 * the same filter / sort params, so every request is the one the old page sent.
 */

/* ------------------------------------------------------------------ columns */

/**
 * kind:   how the cell renders (see SupplierListView `cellContent`).
 * value:  reads the cell value from the API row.
 * filter: { param, input: text | date | number | status | orderType | load | containerStatus, multiple }
 * sort:   the sort param, or null.
 * pin:    the record number -- fixed width, pinned left, always shown.
 * fixed:  keeps its width when the table shares out spare space (Status).
 */
// Every value sits centred in its column (supplier lists only).
const column = (key, label, width, extra = {}) => ({ key, label, width, align: 'center', kind: 'text', filter: null, sort: null, ...extra });

const textCol = (key, label, width, field, param, { sort = true, ...extra } = {}) =>
  column(key, label, width, { value: (o) => o?.[field], filter: { param, input: 'text' }, sort: sort ? param : null, ...extra });

const dateCol = (key, label, field, param) =>
  column(key, label, 124, { kind: 'date', value: (o) => o?.[field], filter: { param, input: 'date' }, sort: param });

const recordCol = (label, field, param, extra = {}) =>
  column('no', label, 124, { pin: true, kind: 'record', value: (o) => o?.[field], filter: { param, input: 'text' }, sort: param, ...extra });

const qtyCol = () => column('qty', 'Qty', 76, {
  kind: 'int', value: (o) => o?.quantity, filter: { param: 'quantity', input: 'number' }, sort: 'quantity',
});

/** Order amount: the invoiced pre-tax total once there is one, else the order amount (selectAmount). */
const orderAmount = (o) => {
  const invoiced = Number(o?.all_invoices_pre_tax_total);
  return invoiced > 0 ? invoiced : Number(o?.displayAmount || 0);
};

const amountCol = () => column('amount', 'Amount', 124, {
  kind: 'money', value: orderAmount, filter: { param: 'amount', input: 'number' }, sort: 'amount',
});

const moneyCol = (key, label, field, { zero = false } = {}) => column(key, label, 132, {
  kind: 'money', value: (o) => (o?.[field] == null && zero ? 0 : o?.[field]), filter: { param: field, input: 'number' }, sort: field,
});

// Multi-select only where the old filter was: Received Orders (OrdersSelectFilter on type `order`) and
// Container Management (load / container status); single-select everywhere else, RFQs and Quotes included.
const statusCol = ({ param = 'status', input = 'status', multiple = false } = {}) => column('status', 'Status', 200, {
  fixed: true, kind: 'status', value: (o) => o?.status?.name, filter: { param, input, multiple }, sort: param,
});

const orderTypeCol = () => column('type', 'Order type', 104, {
  kind: 'type', value: (o) => o?.order_type, filter: { param: 'order_type', input: 'orderType' },
});

const refCol = () => textCol('ref', 'Reference no', 150, 'reference_no', 'reference', {
  value: (o) => o?.customer_provider_number || o?.reference_no,
});
const decedentCol = () => textCol('decedent', 'Decedent', 160, 'decedents', 'decedent');
const deliveryEtaCol = (label = 'Delivery ETA') => dateCol('eta', label, 'eta_date', 'deliveryETA');
const orderDateCol = (label = 'Order date') => dateCol('orderDate', label, 'date', 'date');
const containerCol = ({ sort = false } = {}) => textCol('container', 'Container no', 140, 'container_number', 'container_number', { sort });
const ctnCol = () => textCol('ctn', 'CTN', 140, 'container_tracking_number', 'container_tracking_number');
const customerCodeCol = () => textCol('customer', 'Customer code', 132, 'company_code', 'company_code');
const shipDateCol = () => dateCol('shipDate', 'Shipment date', 'shipment_date', 'shipment_date');
const etaDateCol = () => dateCol('eta', 'ETA date', 'eta_date', 'eta_date');
const weightCol = () => column('weight', 'Weight (lbs)', 112, {
  kind: 'int', value: (o) => o?.weight, filter: { param: 'weight', input: 'number' }, sort: 'weight',
});
// The shipment views' No of Orders had no paramKey, so it never filtered or sorted server-side.
const ordersCountCol = (filter) => column('orders', 'No of orders', 104, {
  kind: 'int', value: (o) => o?.no_of_orders,
  ...(filter ? { filter: { param: 'no_of_orders', input: 'text' }, sort: 'no_of_orders' } : {}),
});
const containerRecordCol = (extra) => recordCol('Container no', 'container_number', 'container_number', { width: 150, ...extra });

/* -------------------------------------------------------------------- lists */

/**
 * One table. `stage` is the request's stage and the record link's stage; `viewFilter` is what
 * OrdersRowActions keys its action set on for the two-view pages.
 * `actionSlots`: the most action icons a row shows. `fixedActions`: the Actions column keeps the
 * width of all its slots (RFQs / Quotes); otherwise it is sized to the busiest row on the page.
 */
export const SUPPLIER_LISTS = {
  rfq: {
    stage: 'rfq', title: 'RFQs', noun: 'RFQs', statusCategory: 'rfq_status', actionSlots: 3, fixedActions: true,
    columns: [
      recordCol('RFQ no', 'order_no', 'orderNo', { passRole: true }), orderDateCol('RFQ date'), refCol(), decedentCol(),
      deliveryEtaCol('Quote ETA'), qtyCol(), statusCol(),
      column('drawing', 'Drawing', 92, { kind: 'drawing', value: (o) => o?.drawing_required_icon === true }),
      column('revision', 'Revision notes', 180, { value: (o) => o?.revision_note }),
    ],
  },
  quote: {
    stage: 'quote', title: 'Quotes', noun: 'quotes', statusCategory: 'quote_status', actionSlots: 3, fixedActions: true,
    columns: [
      recordCol('Quote no', 'order_no', 'orderNo', { passRole: true }), orderDateCol('Quote date'), refCol(), decedentCol(),
      deliveryEtaCol(), qtyCol(), amountCol(), statusCol(),
    ],
  },
  received: {
    stage: 'order', title: 'Received Orders', noun: 'orders', statusCategory: 'order_status', actionSlots: 5, updateStatusValues: true,
    columns: [
      recordCol('Order no', 'order_no', 'orderNo', { passRole: true }), orderDateCol(), refCol(), decedentCol(), deliveryEtaCol(),
      qtyCol(), amountCol(), orderTypeCol(), containerCol(), statusCol({ multiple: true }),
    ],
    hidden: ['container'],
  },
  confirmed: {
    stage: 'confirmed', title: 'Confirmed Orders', noun: 'orders', statusCategory: 'confirmed_status', actionSlots: 5, containerButton: true,
    columns: [
      recordCol('Order no', 'order_no', 'orderNo', { passRole: true }), orderDateCol(), decedentCol(), deliveryEtaCol(), qtyCol(),
      amountCol(), orderTypeCol(), statusCol(),
    ],
  },
  containers: {
    stage: 'container-management', title: 'Container Management', noun: 'containers', actionSlots: 5, create: true, orderSearch: true,
    columns: [
      containerRecordCol({ passRole: true }),
      textCol('customer', 'Customer code', 132, 'customerCode', 'customerCode'),
      orderTypeCol(),
      textCol('location', 'Location', 150, 'location', 'location'),
      column('capacity', 'Capacity', 190, { kind: 'capacity' }),
      ordersCountCol(true),
      shipDateCol(),
      etaDateCol(),
      column('load', 'Load status', 124, {
        kind: 'load', value: (o) => o?.load_status?.name, filter: { param: 'load_status', input: 'load', multiple: true }, sort: 'load_status',
      }),
      statusCol({ input: 'containerStatus', multiple: true }),
    ],
  },
  ipShip: {
    stage: 'in-production', viewFilter: 'inproduction_shipment_view', title: 'In Production', noun: 'containers',
    statusCategory: 'in-production_status', actionSlots: 5,
    columns: [containerRecordCol(), shipDateCol(), etaDateCol(), weightCol(), ordersCountCol(false), statusCol()],
  },
  ipOrders: {
    stage: 'in-production', viewFilter: 'inproduction_orders_view', title: 'In Production', noun: 'orders',
    statusCategory: 'inproduction_orders', actionSlots: 5,
    columns: [
      // PGSS-5804: the rows are orders, so the link is an order's (number and View Order), not the In Production
      // stage's container link. The API views this list's orders under 'order' too.
      recordCol('Order no', 'order_no', 'orderNo', { passRole: true, linkStage: 'order' }), customerCodeCol(), orderDateCol(), deliveryEtaCol(), qtyCol(),
      amountCol(), containerCol(), statusCol(),
    ],
  },
  shippedOrders: {
    stage: 'fulfilled', viewFilter: 'shipped_orders_view', title: 'Shipped Orders', noun: 'orders',
    statusCategory: 'fulfilled_status', actionSlots: 5,
    columns: [
      recordCol('Order no', 'order_no', 'orderNo', { passRole: true }), customerCodeCol(), orderDateCol(), decedentCol(), deliveryEtaCol(),
      qtyCol(), amountCol(), orderTypeCol(), containerCol(), ctnCol(), statusCol(),
    ],
  },
  shippedShip: {
    stage: 'fulfilled', viewFilter: 'shipped_shipment_view', title: 'Shipped Orders', noun: 'containers',
    statusCategory: 'fulfilled_shipments', actionSlots: 5,
    // The old Shipment View showed the container number as plain text, not a link.
    columns: [containerRecordCol({ kind: 'recordText' }), ctnCol(), shipDateCol(), etaDateCol(), weightCol(), ordersCountCol(false), statusCol()],
  },
  invoices: {
    stage: 'invoice', title: 'Invoices', noun: 'invoices', statusCategory: 'invoice_status', actionSlots: 5,
    columns: [
      recordCol('Invoice no', 'invoice_no', 'invoice_no', { width: 140 }),
      dateCol('orderDate', 'Invoice date', 'invoice_date', 'invoice_date'),
      dateCol('dueDate', 'Due date', 'due_date', 'due_date'),
      statusCol({ param: 'invoice_status' }),
      moneyCol('amount', 'Invoice amount', 'invoice_amount'),
      moneyCol('paid', 'Paid amount', 'paid_amount', { zero: true }),
      moneyCol('due', 'Due amount', 'due_amount'),
      containerCol({ sort: true }),
      ctnCol(),
    ],
  },
};

/* -------------------------------------------------------------------- pages */

/**
 * One route. Pages with two views keep the view in the URL's `view_type`, as the old page did
 * (OrderTypeFilter); without one, the first view shows and no view_type is sent.
 */
export const SUPPLIER_PAGES = {
  rfq: { lists: [{ list: 'rfq' }] },
  quote: { lists: [{ list: 'quote' }] },
  order: { lists: [{ list: 'received' }] },
  confirmed: { lists: [{ list: 'confirmed' }] },
  'container-management': { lists: [{ list: 'containers' }] },
  'in-production': {
    lists: [
      { list: 'ipShip', label: 'Shipment View', viewType: 'in-production' },
      { list: 'ipOrders', label: 'Orders View', viewType: 'inproduction-orders' },
    ],
  },
  fulfilled: {
    lists: [
      { list: 'shippedOrders', label: 'Orders View', viewType: 'fulfilled' },
      { list: 'shippedShip', label: 'Shipment View', viewType: 'fulfilled-shipments' },
    ],
  },
  invoice: { lists: [{ list: 'invoices' }] },
};

export const SUPPLIER_PAGE_TYPES = Object.keys(SUPPLIER_PAGES);

export function supplierPage(type) {
  return SUPPLIER_PAGES[type] || null;
}

/** The view the URL names, else the page's first. */
export function supplierPageView(type, search) {
  const page = supplierPage(type);
  if (!page) return null;
  const viewType = new URLSearchParams(search || '').get('view_type');
  return page.lists.find((v) => v.viewType && v.viewType === viewType) || page.lists[0];
}

/** Switching view starts clean, as the old Type dropdown did (it cleared every URL param). */
export function supplierViewSearch(view) {
  return view?.viewType ? `?view_type=${encodeURIComponent(view.viewType)}` : '';
}

export function supplierList(key) {
  return SUPPLIER_LISTS[key] || SUPPLIER_LISTS.rfq;
}

export function supplierListColumns(listKey) {
  return supplierList(listKey).columns;
}

export function supplierListSortParamKey(col) {
  return col?.sort || null;
}

/**
 * The URL `sort` after clicking a column whose sort param is `paramKey`, given the current `sort`.
 * As the old supplier table: a new column sorts ascending first; the same column then toggles.
 */
export function supplierListNextSort(currentSort, paramKey) {
  const [currentKey, currentDir] = String(currentSort || '').split(',');
  const dir = currentKey === paramKey && currentDir === 'ASC' ? 'DESC' : 'ASC';
  return `${paramKey},${dir}`;
}

/** The URL params these columns' filters write, so Reset filters removes exactly these. */
export function listColFilterParams(columns) {
  return (columns || []).filter((c) => c.filter).map((c) => c.filter.param);
}

/** The URL params a supplier list's column filters write, so Reset filters removes exactly these. */
export function supplierListColFilterParams(listKey) {
  return listColFilterParams(supplierListColumns(listKey));
}

/* -------------------------------------------------------------------- tones */

/**
 * Status tone by what the status means for the supplier: their move, a revision asked of them,
 * waiting on someone else, done, or stopped. Keyed on system_code; anything unlisted reads as waiting.
 */
export const SUPPLIER_STATUS_TONE_BY_CODE = {
  RFQ_REQUESTED: 'act',
  RFQ_RE_REQUESTED: 'rev',
  PRODUCT_DRAWING_REQUESTED: 'act',
  QUOTE_REVISIONS_REQUESTED: 'rev',
  QUOTE_REQUESTED: 'act',
  QUOTE_REJECTED: 'stop',
  QUOTE_CANCELED: 'stop',
  ORDER_RECEIVED: 'act',
  ORDER_DRAWING_REQUESTED: 'act',
  ORDER_DRAWING_REREQUESTED: 'rev',
  ORDER_DRAWING_REVISION_REQUESTED: 'rev',
  ORDER_DRAWING_SUBMITTED: 'wait',
  ORDER_OUT_FOR_DRAWING_APPROVAL: 'wait',
  ORDER_DRAWING_APPROVED: 'ok',
  ORDER_IN_PRODUCTION: 'ok',
  INLOADING_SHIPMENT: 'act',
  AWAITING_SHIPMENT: 'wait',
  CONTAINER_APPROVAL_PENDING: 'wait',
  ORDER_IN_PRODUCTION_2: 'act',
  ORDER_READY_TO_SHIP: 'act',
  ORDER_PENDING_PRODUCT_IMAGE_REVIEW: 'act',
  ORDER_WAITING_FOR_PRODUCT_IMAGE_APPROVAL: 'wait',
  SHIPMENT_CONFIRMATION_PENDING: 'wait',
  ORDER_SHIPMENT_APPROVAL: 'wait',
  ORDER_SHIPMENT_REVISION_REQUESTED: 'rev',
  ORDER_IN_LOADING: 'wait',
  ORDER_PENDING_FOR_RECEIVE_SHIPMENT: 'wait',
  ORDER_COMPLETED_AND_SHIPPED: 'ok',
  ORDER_INVOICED: 'ok',
  ORDER_PARTIAL_PAYMENT: 'rev',
  ORDER_COMPLETED_AND_PAID_IN_FULL: 'ok',
  SHIPMENT_COMPLETED: 'ok',
};

export const SUPPLIER_STATUS_TONES = {
  act: { bg: '#eaf0ff', fg: '#1a44a8', border: '#d3e0ff', dot: '#2f6bf0' },
  rev: { bg: '#fff3e6', fg: '#9a4b06', border: '#fde0c0', dot: '#ef8b1c' },
  wait: { bg: '#f1f3f6', fg: '#5f6b7a', border: '#e3e8ef', dot: '#8c97a6' },
  ok: { bg: '#e7f5ec', fg: '#0f6b3d', border: '#c6e8d3', dot: '#1a9457' },
  stop: { bg: '#fdecea', fg: '#b42318', border: '#f7cdc7', dot: '#d92d20' },
};

export function supplierStatusTone(systemCode) {
  return SUPPLIER_STATUS_TONES[SUPPLIER_STATUS_TONE_BY_CODE[systemCode] || 'wait'];
}

/* ------------------------------------------------------------------ actions */

/**
 * The supplier's next step on a row: shown first and highlighted. Matched on the action's name,
 * which is what the configured workflow action renders as its tooltip.
 */
export const SUPPLIER_PRIMARY_ACTIONS = [
  'submit quote', 'submit drawing', 'edit quote', 'move to production', 'add to container', 'load orders',
  'send for approval', 'mark as completed and shipped', 'upload image', 'generate invoice',
];

export function isSupplierPrimaryAction(label) {
  return SUPPLIER_PRIMARY_ACTIONS.includes(String(label || '').trim().toLowerCase());
}

/** Moves the first primary action to the front; the rest keep their configured order. */
export function orderSupplierRowActions(items, labelOf = (item) => item, isPrimary = isSupplierPrimaryAction) {
  const index = items.findIndex((item) => isPrimary(labelOf(item)));
  if (index <= 0) return items;
  return [items[index], ...items.slice(0, index), ...items.slice(index + 1)];
}

/** Icon slots a row with `count` actions takes when it may use `slots`: all of them, or every slot (the last is More). */
export function supplierActionSlotsUsed(count, slots) {
  return Math.min(count || 0, slots);
}

export const SUPPLIER_ACTION_ICON = 28;
export const SUPPLIER_ACTION_GAP = 4;
export const SUPPLIER_ACTION_PAD = 12;

/**
 * Order, shipment and invoice lists drop the Actions column when no row on the page has an action.
 * RFQs / Quotes (`fixedActions`) always keep their three-icon column.
 */
export function supplierListHidesActions(list, maxActions) {
  return !list?.fixedActions && !maxActions;
}

/** Narrowest the Actions column gets: the "Actions" header still reads in full. */
export const SUPPLIER_ACTIONS_MIN_WIDTH = 76;

/** Actions column width for this many icons (one icon's width at least, and never under the header). */
export function supplierActionsWidth(slots) {
  const n = Math.max(1, slots || 0);
  return Math.max(SUPPLIER_ACTIONS_MIN_WIDTH, n * SUPPLIER_ACTION_ICON + (n - 1) * SUPPLIER_ACTION_GAP + 2 * SUPPLIER_ACTION_PAD);
}

/* ------------------------------------------------------------------- layout */

const LAYOUT_KEY = 'supplier-list-layout';

export function supplierListLayoutKey(listKey, userId) {
  return `${LAYOUT_KEY}:${listKey}:${userId || ''}`;
}

/** Visible columns and the column-filter row, per list and user. Never throws (private mode). */
export function readSupplierListLayout(listKey, userId, storage) {
  try {
    const store = storage || window.localStorage;
    const parsed = JSON.parse(store.getItem(supplierListLayoutKey(listKey, userId)) || 'null');
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function writeSupplierListLayout(listKey, userId, layout, storage) {
  try {
    const store = storage || window.localStorage;
    store.setItem(supplierListLayoutKey(listKey, userId), JSON.stringify(layout));
  } catch {
    // private mode / quota
  }
}

/** Stored visibility over the defaults (a list's `hidden` start hidden); the record number always shows. */
export function supplierListVisibleCols(columns, stored, hidden = []) {
  return columns.reduce((acc, c) => {
    const saved = stored?.cols?.[c.key];
    acc[c.key] = c.pin ? true : (typeof saved === 'boolean' ? saved : !hidden.includes(c.key));
    return acc;
  }, {});
}

/* ------------------------------------------------------------------- widths */

export function supplierListTableMinWidth(columns, actionsWidth) {
  return columns.reduce((sum, c) => sum + c.width, 0) + (actionsWidth || 0);
}

/**
 * Pixel width of every visible column so the table fills `availableWidth` exactly. The record number,
 * Actions and `fixed` columns (Status) keep their width; the other columns share the spare width in
 * proportion to their own. With only fixed columns visible, those share it instead, so the table
 * never leaves empty space. Narrower than the columns need (or not measured yet): every column at
 * its own width, and the table scrolls.
 *
 * @returns {{ widths: Record<string, number>, total: number }}
 */
export function supplierListColumnWidths(columns, availableWidth, actionsWidth) {
  const widths = Object.fromEntries(columns.map((c) => [c.key, c.width]));
  const min = supplierListTableMinWidth(columns, actionsWidth);
  const spare = Math.max(0, Math.floor((availableWidth || 0) - min));
  let grow = columns.filter((c) => !c.pin && !c.fixed);
  if (!grow.length) grow = columns.filter((c) => !c.pin);
  if (!spare || !grow.length) return { widths, total: min };
  const growTotal = grow.reduce((sum, c) => sum + c.width, 0);
  // Shared on a running total, so rounding never piles up on one column and the sum is exact.
  let before = 0;
  let given = 0;
  grow.forEach((c) => {
    before += c.width;
    const upTo = Math.floor((spare * before) / growTotal);
    widths[c.key] = c.width + (upTo - given);
    given = upTo;
  });
  return { widths, total: min + spare };
}
